import React from 'react';

/**
 * FlyingEaves — a stylised traditional Chinese roof with upturned eaves (飛簷).
 * Rendered as an inline SVG silhouette so it stays crisp at any size and can be
 * recoloured via `color`. Used as a signature motif across sections.
 */
export const FlyingEaves: React.FC<{ className?: string; color?: string }> = ({
  className = '',
  color = 'currentColor',
}) => (
  <svg
    viewBox="0 0 240 70"
    className={className}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Ridge line with ornaments */}
    <path
      d="M12 46 C 40 40, 70 38, 120 38 C 170 38, 200 40, 228 46"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Left upturned eave */}
    <path
      d="M12 46 C 4 40, 2 30, 8 24 C 10 30, 16 34, 24 36"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Right upturned eave */}
    <path
      d="M228 46 C 236 40, 238 30, 232 24 C 230 30, 224 34, 216 36"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
    />
    {/* Roof body */}
    <path
      d="M28 46 L 120 30 L 212 46"
      stroke={color}
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Central roof ornament (寶頂) */}
    <path d="M120 30 L 120 20" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <circle cx="120" cy="16" r="3" stroke={color} strokeWidth="1.6" />
    {/* Ridge tiles */}
    <path
      d="M40 47 L 200 47 M46 52 L 194 52"
      stroke={color}
      strokeWidth="1.2"
      strokeOpacity="0.55"
      strokeLinecap="round"
    />
    {/* Pillars */}
    <path d="M60 52 L 60 68 M120 52 L 120 68 M180 52 L 180 68" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeOpacity="0.8" />
  </svg>
);

/**
 * CloudScroll — a single 如意雲紋 (ruyi cloud) unit, used for dividers & accents.
 */
export const CloudScroll: React.FC<{ className?: string; color?: string }> = ({
  className = '',
  color = 'currentColor',
}) => (
  <svg viewBox="0 0 80 40" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M6 24 C 6 16, 14 12, 20 16 C 22 8, 34 8, 36 18 C 44 12, 54 16, 52 26 C 62 22, 74 26, 74 34"
      stroke={color}
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <circle cx="20" cy="20" r="2" fill={color} />
    <circle cx="52" cy="26" r="2" fill={color} />
  </svg>
);

/**
 * SectionTitle — consistent Chinese-style section header with eyebrow, big serif
 * title, and an ornate gold rule.
 */
export const SectionTitle: React.FC<{
  eyebrow?: string;
  title: string;
  align?: 'center' | 'left';
  light?: boolean;
}> = ({ eyebrow, title, align = 'center', light = false }) => (
  <div className={align === 'center' ? 'text-center' : 'text-left'}>
    {eyebrow && (
      <span className={`eyebrow ${light ? 'text-brand-gold' : ''} mb-4`}>
        <span className="w-6 h-[1px] bg-current opacity-60" />
        {eyebrow}
        <span className="w-6 h-[1px] bg-current opacity-60" />
      </span>
    )}
    <h2
      className={`text-3xl md:text-4xl lg:text-5xl font-serif leading-tight mt-2 ${
        light ? 'text-brand-paper' : 'text-brand-ink'
      }`}
    >
      {title}
    </h2>
    <div className={`mt-6 flex items-center gap-3 ${align === 'center' ? 'justify-center' : 'justify-start'}`}>
      <span className="rule-cn" />
    </div>
  </div>
);

/**
 * SealMark — the vermilion authenticity seal (印章). Uses the generated seal image.
 */
export const SealMark: React.FC<{ src: string; className?: string; label?: string }> = ({
  src,
  className = '',
  label = '正宗',
}) => (
  <div className={`relative inline-block ${className}`} title={label}>
    <img src={src} alt={label} className="w-full h-full object-contain seal-pulse mix-blend-multiply" />
  </div>
);

export default FlyingEaves;
